import React, { ChangeEvent } from 'react';
import { isEmpty, map } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { AccessLevel, JourneySummary } from '@dev/ee-api/ee-api-models';
import {
    Typography,
    SimplePopover,
    CustomTableHeadersProps,
    TertiaryButton,
    palette,
    CustomTableRowsProps,
    ContentLoader,
    DotsIcon,
    CustomTable,
    TableRowNameLink,
} from '@dev/ee-components';
import { DeleteAutomation, getAutomationStore, PickAutomations } from '@/store/automations/actions';
import { DropdownOptions } from '@/store/automations/types';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { getAccountStore } from '@/store/account/actions';
import { StatusBadge } from '@/assets/StatusBadge';
import { useDropdownActions } from '@/components/Automations/AutomationsList/hooks/useDropdownActions';
import TableFiltersComponent from '../TableFilters';

const TableContainer = styled.div`
    margin-top: 32px;
`;

const useTableHeaders = (): CustomTableHeadersProps[] => {
    const { t } = useTranslation();
    const automations = useSelector(getAutomationStore);
    const { GeneralInfo } = useSelector(getAccountStore);
    const dispatch = useDispatch();
    const { checkedAutomations } = automations;
    const checkedAutomationsLength = checkedAutomations.length;

    const dialogParams: ShowDialogParams = {
        title: `${t('delete')} ${checkedAutomationsLength} ${
            checkedAutomationsLength > 1 ? t('automations').toLowerCase() : t('automation').toLowerCase()
        }`,
        destructiveLabel: t('delete'),
        onClickDestructive: (): void => {
            dispatch(DeleteAutomation(checkedAutomations));
        },
        cancelLabel: t('cancel'),
        body: t('deleteautomationsmessage'),
    };
    if (checkedAutomationsLength) {
        const accountAl = GeneralInfo?.AccountAccessLevel;
        return [
            { id: 'automationName', headerAlign: 'left', columnAlign: 'left', label: t('name') },
            {
                id: 'status',
                headerAlign: 'center',
                label: '',
                columnAlign: 'center',
            },
            {
                id: 'icon',
                numeric: false,
                label: '',
                filterIcon: accountAl?.includes?.(AccessLevel.ModifyAutomations) && (
                    <TertiaryButton
                        style={{ margin: 0, color: palette.red[6] }}
                        text={t('delete')}
                        onClick={() => {
                            dispatch(ShowDialog(dialogParams));
                        }}
                    />
                ),
            },
        ];
    }
    return [
        { id: 'automationName', headerAlign: 'left', columnAlign: 'left', label: t('name') },
        {
            id: 'status',
            headerAlign: 'center',
            label: t('status'),
            columnAlign: 'center',
        },
        { id: 'icon', numeric: false, label: '' },
    ];
};

const useTableRows = (): CustomTableRowsProps[] => {
    const { t } = useTranslation();
    const automations = useSelector(getAutomationStore);
    const { GeneralInfo } = useSelector(getAccountStore);
    const { pagination } = automations;
    const dropdownActions = useDropdownActions();
    const accountAl = GeneralInfo?.AccountAccessLevel;
    if (automations?.loading) {
        const perPage = pagination?.perPage || 5;
        const rows: CustomTableRowsProps[] = [];

        for (let index = 0; index < perPage; index++) {
            rows?.push({
                automationName: <ContentLoader mode="row" height={20} />,
                status: <ContentLoader mode="row" height={20} />,
                icon: <ContentLoader mode="row" height={20} />,
            });
        }

        return rows;
    }

    return map(automations?.collection, (automation: JourneySummary, index): CustomTableRowsProps => {
        const rowPopoverOptions = [
            {
                label: t('edit'),
                value: DropdownOptions.Edit,
            },
            {
                label: t('delete'),
                color: '#ff0000',
                value: DropdownOptions.Delete,
            },
        ];

        return {
            automationName: (
                <div style={{ wordBreak: 'break-all', paddingRight: '10px' }}>
                    <Typography variant="caption">
                        <TableRowNameLink onClick={() => dropdownActions({ value: DropdownOptions.Edit }, automation)}>
                            {automation.Name}
                        </TableRowNameLink>
                    </Typography>
                </div>
            ),
            status: <StatusBadge key={`status-${index}`} value={t('draft') as string} status="Draft" borderDisabled />,
            icon: accountAl?.includes(AccessLevel.ModifyAutomations) && (
                <div
                    onClick={(ev): void => {
                        ev.stopPropagation();
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <SimplePopover
                        type="icon"
                        options={rowPopoverOptions}
                        enableCheckbox={false}
                        onItemClick={(ev, option: { label: string; value: DropdownOptions }): void => {
                            dropdownActions(option, automation);
                        }}
                    >
                        <DotsIcon />
                    </SimplePopover>
                </div>
            ),
            rowID: automation.Name,
        };
    });
};

const columnWidth: Record<string, string> = {
    automationName: '85%',
    status: '130px',
    icon: '50px',
};

const AutomationsDraftsTable = (): JSX.Element => {
    const automations = useSelector(getAutomationStore);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const pickAutomation = (event: ChangeEvent<unknown>, pickedAutomations: string[]) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();

        dispatch(PickAutomations(pickedAutomations));
    };
    const headers = useTableHeaders();
    const rows = useTableRows();

    return (
        <TableContainer>
            <TableFiltersComponent />
            <CustomTable
                headers={headers}
                rows={rows}
                isEmpty={isEmpty(automations?.collection) && !automations?.loading}
                loading={automations?.loading}
                stickyHeader
                rowPadding={48}
                rowHeight={60}
                columnsWidth={columnWidth}
                emptyStateProps={{ text: t('emptystate') }}
                disableFirstRowBorderTop
                onSelectRow={pickAutomation}
                selectedRows={automations.checkedAutomations}
                isHeaderCheckboxEnabled
                disableRowClick
            />
        </TableContainer>
    );
};

export default AutomationsDraftsTable;
